import {
  FETCH_CAMPAIGNS,
  FETCH_CAMPAIGNS_SUCCESS,
  FETCH_CAMPAIGNS_FAILED,
  FETCH_NOTIFICATIONS,
  FETCH_NOTIFICATIONS_SUCCESS,
  FETCH_NOTIFICATIONS_FAILED,
  EDIT_NOTIFICATIONS,
  SEND_NOTIFICATION,
  SEND_NOTIFICATION_SUCCESS,
  SEND_NOTIFICATION_FAILED,
  FETCH_SCHEDULED_NOTIFICATIONS,
  FETCH_SCHEDULED_NOTIFICATIONS_SUCCESS,
  FETCH_SCHEDULED_NOTIFICATIONS_FAILED,
  EDIT_SCHEDULED_NOTIFICATION,
} from "../store/types";
import { RequestPushMsg } from '../other/NotificationFunctions';
import { firebase } from '../config/configureFirebase';
import { onValue, off, get, set, push, remove, query, orderByChild, limitToLast } from "firebase/database";
import store from '../store/store';

export const fetchUserNotifications = () => (dispatch) => {

  const {
    auth,
    userNotificationsRef
  } = firebase;

  dispatch({
    type: FETCH_NOTIFICATIONS,
    payload: null
  });

  const uid = auth.currentUser.uid;

  off(userNotificationsRef(uid)); // never two listeners on the node (P2-2)
  onValue(userNotificationsRef(uid), snapshot => {
    const data = snapshot.val(); 
    if(data){
      const arr = Object.keys(data).map(i => {
        data[i].id = i
        return data[i]
      });
      dispatch({
        type: FETCH_NOTIFICATIONS_SUCCESS,
        payload: arr.reverse()
      });
    } else {
      dispatch({
        type: FETCH_NOTIFICATIONS_FAILED,
        payload: "No data available."
      });
    }
  });
};

export const fetchNotifications = () => async (dispatch) => {

  const {
    notifyRef
  } = firebase;

  dispatch({
    type: FETCH_NOTIFICATIONS,
    payload: null
  });
  try {
    const snapshot = await get(notifyRef);
    if (snapshot.val()) {
      const data = snapshot.val();

      const arr = Object.keys(data).map(i => {
        data[i].id = i
        return data[i]
      });

      dispatch({
        type: FETCH_NOTIFICATIONS_SUCCESS,
        payload: arr
      });
    } else {
      dispatch({
        type: FETCH_NOTIFICATIONS_FAILED,
        payload: "No data available."
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_NOTIFICATIONS_FAILED,
      payload: error.message
    });
  }
};

export const editNotifications = (notification, method) => (dispatch) => {
  const {
    notifyRef,
    notifyEditRef
  } = firebase;
  dispatch({
    type: EDIT_NOTIFICATIONS,
    payload: { method, notification }
  });
  if (method === 'Add') {
    push(notifyRef, notification);
  } else if (method === 'Delete') {
    remove(notifyEditRef(notification.id));
  } else {
    set(notifyEditRef(notification.id), notification);
  }
}

// Send a bulk notification NOW.
//
// opts.requestId — generated ONCE per form submission by the caller and reused
//   on every retry of that submission. The server derives the campaign id from
//   it, so a replayed POST (browser timeout, impatient second click, a proxy
//   retry) can never create a second campaign. Without it the old flow was a
//   clean double-send: the request awaited the entire fan-out, and the 5-minute
//   recipient cache guaranteed the second run hit exactly the same people.
// opts.force   — the caller has seen the "identical message sent recently"
//   warning and means it.
//
// RESOLVES with the server's JSON rather than only dispatching, so the view can
// act on `duplicate: true` before anything goes out. A rejected promise means
// the request never reached the server.
export const sendNotification = (notification, opts) => async (dispatch) => {

  const {
    config
  } = firebase;

  dispatch({
    type: SEND_NOTIFICATION,
    payload: notification
  });

  const settings = store.getState().settingsdata.settings;
  let host = window && window.location && settings.CompanyWebsite === window.location.origin? window.location.origin : `https://${config.projectId}.web.app`
  let url = `${host}/send_notification`;

  try {
    const response = await fetch(url, {
        method: 'POST',
        headers: {
            'Content-Type': 'application/json',
        },
        body: JSON.stringify({
            "notification": notification,
            "requestId": (opts && opts.requestId) || undefined,
            "force": (opts && opts.force) === true
        })
    });
    const result = await response.json();
    if (result && result.duplicate === true) {
      // Not an error and not a send — the view asks the operator to confirm.
      dispatch({
        type: SEND_NOTIFICATION_FAILED,
        payload: result.message || 'An identical notification was sent recently.'
      });
      return result;
    }
    dispatch({
      type: SEND_NOTIFICATION_SUCCESS,
      payload: result
    });
    return result;
  } catch (error) {
    dispatch({
      type: SEND_NOTIFICATION_FAILED,
      payload: error.message || 'Notification send failed'
    });
    throw error;
  }
}

export const fetchScheduledNotifications = () => async (dispatch) => {
  const {
    scheduledNotifyRef
  } = firebase;

  dispatch({
    type: FETCH_SCHEDULED_NOTIFICATIONS,
    payload: null
  });

  try {
    const snapshot = await get(scheduledNotifyRef);
    if (snapshot.val()) {
      const data = snapshot.val();
      const arr = Object.keys(data).map(i => {
        data[i].id = i;
        return data[i];
      });
      dispatch({
        type: FETCH_SCHEDULED_NOTIFICATIONS_SUCCESS,
        payload: arr
      });
    } else {
      dispatch({
        type: FETCH_SCHEDULED_NOTIFICATIONS_FAILED,
        payload: "No scheduled notifications."
      });
    }
  } catch (error) {
    dispatch({
      type: FETCH_SCHEDULED_NOTIFICATIONS_FAILED,
      payload: error.message
    });
  }
};

export const scheduleNotification = (notification) => (dispatch) => {
  const {
    scheduledNotifyRef
  } = firebase;
  dispatch({
    type: EDIT_SCHEDULED_NOTIFICATION,
    payload: { method: 'Add', notification }
  });
  push(scheduledNotifyRef, notification);
};

export const editScheduledNotification = (notification) => (dispatch) => {
  const {
    scheduledNotifyEditRef
  } = firebase;
  dispatch({
    type: EDIT_SCHEDULED_NOTIFICATION,
    payload: { method: 'Update', notification }
  });
  set(scheduledNotifyEditRef(notification.id), notification);
};

export const deleteScheduledNotification = (notification) => (dispatch) => {
  const {
    scheduledNotifyEditRef
  } = firebase;
  dispatch({
    type: EDIT_SCHEDULED_NOTIFICATION,
    payload: { method: 'Delete', notification }
  });
  remove(scheduledNotifyEditRef(notification.id));
};

/* ------------------------------------------------------------------ *
 * Bulk delivery campaigns
 *
 * A campaign is one bulk send — from "Send now" or from a scheduled
 * notification firing — and it owns the per-recipient delivery ledger. These
 * records are the answer to "what actually happened to that notification",
 * which /packets could never answer: packets carried no campaign id, no state
 * and no counters, so a queued send was invisible until it either arrived or
 * did not.
 *
 * Only the small aggregate is readable here. Recipient slices and the ledger
 * stay server-side, so watching a campaign costs a few hundred bytes per
 * update rather than the audience.
 * ------------------------------------------------------------------ */

// Live view of the most recent campaigns. Returns the unsubscribe function —
// the caller MUST call it when leaving the tab, so an idle dashboard is not
// streaming delivery updates it nobody is reading.
export const watchCampaigns = (limit = 25) => (dispatch) => {
  const { campaignsRef } = firebase;
  dispatch({ type: FETCH_CAMPAIGNS, payload: null });
  const q = query(campaignsRef, orderByChild('createdAt'), limitToLast(limit));
  return onValue(q, (snapshot) => {
    const data = snapshot.val();
    if (!data) {
      dispatch({ type: FETCH_CAMPAIGNS_SUCCESS, payload: [] });
      return;
    }
    const arr = Object.keys(data)
      .map((id) => ({ ...data[id], id }))
      .sort((a, b) => (b.createdAt || 0) - (a.createdAt || 0));
    dispatch({ type: FETCH_CAMPAIGNS_SUCCESS, payload: arr });
  }, (error) => {
    dispatch({ type: FETCH_CAMPAIGNS_FAILED, payload: error.message });
  });
};

const callCampaignEndpoint = async (endpoint, campaignId) => {
  const { auth, config } = firebase;
  const user = auth && auth.currentUser;
  if (!user) throw new Error('Not signed in');
  const idToken = await user.getIdToken(false);
  const settings = store.getState().settingsdata.settings;
  const host = (typeof window !== 'undefined' && window.location && settings && settings.CompanyWebsite === window.location.origin)
    ? window.location.origin
    : `https://${config.projectId}.web.app`;
  const res = await fetch(`${host}/${endpoint}`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Authorization': 'Bearer ' + idToken },
    body: JSON.stringify({ campaignId }),
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) throw Object.assign(new Error(json.error || `Request failed (${res.status})`), json);
  return json;
};

// Stop an in-flight campaign. Workers check the campaign state between flush
// windows, so delivery really halts — within one window, not eventually.
export const cancelCampaign = (campaignId) => callCampaignEndpoint('cancel_campaign', campaignId);

// Re-queue ONLY the recipients whose ledger entry is a retryable failure.
// Devices that are permanently gone are skipped, and anyone already delivered
// is never touched.
export const retryCampaign = (campaignId) => callCampaignEndpoint('retry_campaign', campaignId);
